import styled, { css, keyframes } from 'styled-components';

import { PanelType } from './DetailPanel';

const BOTTOM_NAV_HEIGHT = 72;
const PANEL_GAP = 24;
const PANEL_BOTTOM_OFFSET = BOTTOM_NAV_HEIGHT + PANEL_GAP;

const panelAppear = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const getCornerColor = (type?: PanelType) => {
  switch (type) {
    case 'profit':
      return 'rgba(80, 255, 120, 0.7)';
    case 'loss':
      return 'rgba(255, 80, 80, 0.7)';
    default:
      return 'rgba(255, 255, 255, 0.5)';
  }
};

const getBadgeStyles = (type: PanelType) => {
  switch (type) {
    case 'profit':
      return css`
        border-color: rgba(80, 255, 120, 0.5);
        color: #5f5;
        background: rgba(80, 255, 120, 0.1);
      `;
    case 'loss':
      return css`
        border-color: rgba(255, 80, 80, 0.5);
        color: #f55;
        background: rgba(255, 80, 80, 0.1);
      `;
    case 'ship':
      return css`
        border-color: rgba(255, 200, 100, 0.5);
        color: rgba(255, 200, 100, 0.9);
        background: rgba(255, 200, 100, 0.1);
      `;
    case 'info':
    default:
      return css`
        border-color: rgba(255, 255, 255, 0.25);
        color: rgba(255, 255, 255, 0.7);
        background: rgba(255, 255, 255, 0.05);
      `;
  }
};

export const Container = styled.div<{ $isActive: boolean; $type?: PanelType }>`
  position: fixed;
  bottom: ${PANEL_BOTTOM_OFFSET}px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 20, 20, 0.7);
  padding: 0;
  min-width: 280px;
  max-width: 90vw;
  z-index: 15;
  display: none;

  ${({ $isActive }) =>
    $isActive &&
    css`
      display: block;
      animation: ${panelAppear} 0.25s ease-out;
    `}

  @media (max-width: 768px) {
    bottom: ${PANEL_BOTTOM_OFFSET}px;
    left: 15px;
    right: auto;
    min-width: 220px;
    max-width: 250px;
    transform: none;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    bottom: 84px;
    left: 50%;
    transform: translateX(-50%);
    min-width: 200px;
    max-width: 280px;
  }
`;

export const Corner = styled.div<{ $type?: PanelType }>`
  position: absolute;
  width: 14px;
  height: 14px;
  border-color: ${({ $type }) => getCornerColor($type)};
  border-style: solid;
  border-width: 0;

  &.tl {
    top: 0;
    left: 0;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  &.tr {
    top: 0;
    right: 0;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  &.bl {
    bottom: 0;
    left: 0;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  &.br {
    bottom: 0;
    right: 0;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    width: 10px;
    height: 10px;
  }
`;

export const Header = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 8px 10px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 6px 8px;
  }
`;

export const Badge = styled.div<{ $type: PanelType }>`
  padding: 0 12px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 2px;
  text-transform: uppercase;
  ${({ $type }) => getBadgeStyles($type)}

  @media (max-width: 768px) {
    height: 26px;
    font-size: 9px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    height: 20px;
    font-size: 7px;
    padding: 0 6px;
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.5);
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 100, 100, 0.4);
    color: #f66;
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 16px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    width: 20px;
    height: 20px;
    font-size: 14px;
  }
`;

export const Body = styled.div`
  padding: 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    padding: 8px;
  }
`;
